import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  Wrap,
} from "@chakra-ui/react";
import { useMemo, useRef } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { PRESTATIONS_PRICES_TYPES } from "../../../../../constants/prestations";
import {
  SUBSCRIPTIONS_TYPES,
  SUBSCRIPTIONS_TYPES_VALUE_PER_CUSTOMER,
  SUBSCRIPTIONS_TYPES_VALUE_PER_HOLDER,
} from "../../../../../constants/subscriptions";
import Form from "./Form";
import Handler, { getSubscriptionSelectFormID } from "./Handler";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Subscription").Subscription} subscription
 * @property {import("@raiden/library/types/Holder").Holder[]} holders
 * @property {boolean} isSelected
 * @property {string[]} initialHolderIds
 * @property {(param0: { subscriptionId: string; holderIds: string[] }) => void} upsertSubscriptionsData
 * @property {(param0: { subscriptionId: string }) => void} deleteSubscriptionsData
 * @property {boolean} isOpen
 * @property {() => void} onClose
 *
 * @param {Props} props
 */
function DialogSelect({
  subscription,
  holders: _holders,
  initialHolderIds,
  upsertSubscriptionsData: upsertSubscriptionData,
  deleteSubscriptionsData: deleteSubscriptionData,
  isOpen,
  onClose,
}) {
  const intl = useIntl();

  /** @type {import("react").MutableRefObject<HTMLButtonElement | null>} */
  const ref = useRef(null);

  const holders = useMemo(() => {
    return _holders.filter((holder) =>
      subscription.prestations_holder_types?.includes(holder.type),
    );
  }, [_holders, subscription.prestations_holder_types]);

  const backgroundColor = useColorModeValue("gray.50", "gray.600");

  return (
    <Modal
      initialFocusRef={ref}
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="xl"
      isCentered>
      <ModalOverlay />

      <ModalContent mx="1rem">
        <ModalHeader>
          <FormattedMessage defaultMessage="Souscription à la cotisation" />
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Stack spacing="1rem">
            <Stack
              spacing=".5rem"
              backgroundColor={backgroundColor}
              borderRadius="0.375rem"
              p="1.25rem">
              <Text fontSize="1.25rem" fontWeight={400} lineHeight="1.75rem">
                {subscription.name}
              </Text>

              <Box>
                <Tag
                  variant="solid"
                  colorScheme="teal"
                  borderRadius="0.75rem"
                  py="0.25rem">
                  {SUBSCRIPTIONS_TYPES[subscription.type]
                    ? intl.formatMessage(
                        SUBSCRIPTIONS_TYPES[subscription.type].label,
                      )
                    : subscription.type}
                </Tag>
              </Box>

              {subscription.type === SUBSCRIPTIONS_TYPES_VALUE_PER_CUSTOMER && (
                <Text fontSize="0.875rem" fontWeight={600} lineHeight="1.25rem">
                  <FormattedMessage defaultMessage="Cette cotisation sera valable pour tous les membres du foyer." />
                </Text>
              )}

              {subscription.type === SUBSCRIPTIONS_TYPES_VALUE_PER_HOLDER && (
                <Text fontSize="0.875rem" fontWeight={600} lineHeight="1.25rem">
                  <FormattedMessage defaultMessage="Cette cotisation sera valable uniquement pour les adhérents inscrits." />
                </Text>
              )}

              {subscription.infos && <Text>{subscription.infos}</Text>}

              <Stack spacing=".5rem" alignItems="flex-end">
                {subscription.estimated_price && (
                  <>
                    <Text>
                      <FormattedNumber
                        value={subscription.estimated_price.amount}
                        style="currency"
                        currency="EUR"
                      />
                    </Text>

                    <Text
                      fontSize="1rem"
                      fontWeight={400}
                      lineHeight="1.25rem"
                      textAlign="right">
                      {intl.formatMessage(
                        PRESTATIONS_PRICES_TYPES[
                          subscription.estimated_price.type
                        ].label,
                      )}
                    </Text>
                  </>
                )}
              </Stack>
            </Stack>

            <Handler
              subscription={subscription}
              initialHolderIds={initialHolderIds}
              upsertSubscriptionData={upsertSubscriptionData}
              deleteSubscriptionData={deleteSubscriptionData}
              onClose={onClose}
              render={({ form, isLoading }) => (
                <Form subscription={subscription} holders={holders} />
              )}
            />
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Wrap>
            <Button onClick={onClose}>
              <FormattedMessage defaultMessage="Fermer" />
            </Button>

            <Button
              type="submit"
              form={getSubscriptionSelectFormID({ subscription })}
              ref={ref}
              colorScheme="brandSecondary">
              <FormattedMessage defaultMessage="Confirmer" />
            </Button>
          </Wrap>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default DialogSelect;
